import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { createHabit } from '@/lib/firebase/queries/habits';
import { habitBadges, HABIT_CATEGORY_ORDER } from '@/lib/constants/habitBadges';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { HabitPresetSelector } from './HabitPresetSelector';
import { FrequencySelector } from './FrequencySelector';
import { StepsLinkField } from './StepsLinkField';
import type { HabitPreset, HabitFrequencyConfig } from '@/types/habits';

// UI Components
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

// Form Schema
const formSchema = z.object({
  name: z.string().min(2, 'Habit name must be at least 2 characters'),
  category: z.enum(['Exercises', 'Nutrition', 'Sleep', 'Stress', 'Relations', 'Addictions'] as const),
  isPrivate: z.boolean().optional()
});

interface AddHabitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onHabitCreated?: () => void;
}

export function AddHabitDialog({ open, onOpenChange, onHabitCreated }: AddHabitDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [frequencyConfig, setFrequencyConfig] = useState<HabitFrequencyConfig>({
    frequency: 'daily',
    timesPerFrequency: 1,
    duration: 90,
  });
  const [habitDescription, setHabitDescription] = useState<string | undefined>();
  const [habitDescriptionAr, setHabitDescriptionAr] = useState<string | undefined>();
  const [stepsTarget, setStepsTarget] = useState<number | undefined>(undefined);
  const { currentUser } = useAuth();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      category: 'Exercises',
      isPrivate: false
    },
  });

  // Transient (non-form) dialog state must not survive a close — cancel, the
  // X button, overlay click, and Escape all route through Dialog's
  // onOpenChange, so resetting there (rather than only on successful submit)
  // covers every dismissal path. A leaked stepsTarget in particular would
  // silently make the next unrelated habit auto-complete by steps.
  const resetTransientState = () => {
    setFrequencyConfig({
      frequency: 'daily',
      timesPerFrequency: 1,
      duration: 90,
    });
    setHabitDescription(undefined);
    setHabitDescriptionAr(undefined);
    setStepsTarget(undefined);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      resetTransientState();
    }
    onOpenChange(next);
  };

  const handlePresetSelect = (preset: HabitPreset) => {
    form.setValue('name', isRTL && preset.nameAr ? preset.nameAr : preset.name);
    form.setValue('category', preset.category);
    setHabitDescription(preset.description);
    setHabitDescriptionAr(preset.descriptionAr);
    // If preset has frequency config, use it
    if (preset.frequencyConfig) {
      setFrequencyConfig(preset.frequencyConfig);
    }
    // Optionally close presets panel after selection
    // setShowPresets(false);
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!currentUser) {
      toast.error(t('habits.notifications.loginRequired'));
      return;
    }

    try {
      setIsLoading(true);
      
      // Automatically select the first bronze badge for the chosen category
      const defaultBadge = habitBadges[values.category].badges.bronze[0].id;
      
      await createHabit({
        ...values,
        badge: defaultBadge,
        userId: currentUser.uid,
        startDate: new Date(),
        frequencyConfig,
        description: habitDescription,
        descriptionAr: habitDescriptionAr,
        stepsTarget,
      });
      toast.success(t('habits.notifications.habitCreated'));
      onHabitCreated?.();
      handleOpenChange(false);
    } catch (error) {
      console.error('Error creating habit:', error);
      toast.error(t('habits.notifications.createError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`w-screen h-[100dvh] sm:w-[90vw] ${showPresets ? 'sm:max-w-5xl' : 'sm:max-w-md'} sm:h-auto sm:max-h-[90vh] flex flex-col p-4 sm:p-6 [&>button]:hidden`}>
        <div className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-4 sm:top-6 z-50`}>
          <DialogClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-4 w-4 sm:h-5 sm:w-5"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>
        <DialogHeader className={`pb-4 ${isRTL ? 'text-right pr-0' : 'text-left pl-0 pr-8 sm:pr-12'}`}>
          <DialogTitle className={`text-xl ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('habits.addNewHabit')}
          </DialogTitle>
          <DialogDescription className={`text-sm ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('habits.addNewHabitDesc')}
          </DialogDescription>
        </DialogHeader>

        {/* Two-column layout */}
        <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
          {/* Left Column: Form - 40% width when presets shown */}
          <div className={`${showPresets ? 'md:w-[40%]' : 'w-full'} min-w-0 flex-shrink-0`}>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 h-full">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('habits.habitName')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          placeholder={t('habits.habitNamePlaceholder')} 
                          disabled={isLoading} 
                          dir={isRTL ? 'rtl' : 'ltr'}
                          className={`h-10 ${isRTL ? 'text-right' : 'text-left'}`}
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <StepsLinkField value={stepsTarget} onChange={setStepsTarget} />

                {/* Category and Frequency in same row */}
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('habits.category')}
                        </FormLabel>
                        <Select
                          disabled={isLoading}
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger 
                              dir={isRTL ? 'rtl' : 'ltr'} 
                              className={`h-10 ${isRTL ? 'text-right' : 'text-left'}`}
                            >
                              <SelectValue placeholder={t('habits.selectCategory')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent dir={isRTL ? 'rtl' : 'ltr'} className={isRTL ? 'text-right' : 'text-left'}>
                            {HABIT_CATEGORY_ORDER.map((category) => (
                              <SelectItem key={category} value={category} dir={isRTL ? 'rtl' : 'ltr'}>
                                <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                  <span>{habitBadges[category].icon}</span>
                                  <span>{t(`habits.categories.${category}`)}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Frequency Type */}
                  <div className="space-y-2">
                    <Label className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      {isRTL ? 'التكرار' : 'Frequency'}
                    </Label>
                    <Select
                      value={frequencyConfig.frequency}
                      onValueChange={(freq: any) => setFrequencyConfig({
                        ...frequencyConfig,
                        frequency: freq,
                        customDays: freq === 'custom' ? frequencyConfig.customDays || [1, 3, 5] : undefined,
                      })}
                      disabled={isLoading}
                    >
                      <SelectTrigger className={`h-10 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">{isRTL ? 'يومي' : 'Daily'}</SelectItem>
                        <SelectItem value="weekly">{isRTL ? 'أسبوعي' : 'Weekly'}</SelectItem>
                        <SelectItem value="monthly">{isRTL ? 'شهري' : 'Monthly'}</SelectItem>
                        <SelectItem value="custom">{isRTL ? 'مخصص' : 'Custom'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Times Per Frequency and Duration in same row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      {frequencyConfig.frequency === 'daily' && (isRTL ? 'مرات/يوم' : 'Times/Day')}
                      {frequencyConfig.frequency === 'weekly' && (isRTL ? 'مرات/أسبوع' : 'Times/Week')}
                      {frequencyConfig.frequency === 'monthly' && (isRTL ? 'مرات/شهر' : 'Times/Month')}
                      {frequencyConfig.frequency === 'custom' && (isRTL ? 'مرات/يوم' : 'Times/Day')}
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={frequencyConfig.timesPerFrequency}
                      onChange={(e) => setFrequencyConfig({
                        ...frequencyConfig,
                        timesPerFrequency: Math.max(1, parseInt(e.target.value) || 1)
                      })}
                      disabled={isLoading}
                      className={`h-10 ${isRTL ? 'text-right' : 'text-left'}`}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      {isRTL ? 'المدة (أيام)' : 'Duration (Days)'}
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      value={frequencyConfig.duration}
                      onChange={(e) => setFrequencyConfig({
                        ...frequencyConfig,
                        duration: Math.max(1, parseInt(e.target.value) || 1)
                      })}
                      disabled={isLoading}
                      className={`h-10 ${isRTL ? 'text-right' : 'text-left'}`}
                    />
                  </div>
                </div>

                {/* Custom days selector */}
                {frequencyConfig.frequency === 'custom' && (
                  <div className="space-y-2">
                    <Label className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      {isRTL ? 'اختر الأيام' : 'Select Days'}
                    </Label>
                    <div className="grid grid-cols-7 gap-2">
                      {[
                        { value: 0, labelEn: 'Su', labelAr: 'أحد' },
                        { value: 1, labelEn: 'Mo', labelAr: 'إثن' },
                        { value: 2, labelEn: 'Tu', labelAr: 'ثلا' },
                        { value: 3, labelEn: 'We', labelAr: 'أرب' },
                        { value: 4, labelEn: 'Th', labelAr: 'خمي' },
                        { value: 5, labelEn: 'Fr', labelAr: 'جمع' },
                        { value: 6, labelEn: 'Sa', labelAr: 'سبت' },
                      ].map((day) => (
                        <div
                          key={day.value}
                          className={cn(
                            "flex flex-col items-center gap-1 p-2 rounded-md border cursor-pointer transition-colors",
                            frequencyConfig.customDays?.includes(day.value)
                              ? "bg-primary text-primary-foreground border-primary"
                              : "hover:bg-muted",
                            isLoading && "opacity-50 cursor-not-allowed"
                          )}
                          onClick={() => {
                            if (isLoading) return;
                            const currentDays = frequencyConfig.customDays || [];
                            const newDays = currentDays.includes(day.value)
                              ? currentDays.filter(d => d !== day.value)
                              : [...currentDays, day.value].sort();
                            if (newDays.length > 0) {
                              setFrequencyConfig({ ...frequencyConfig, customDays: newDays });
                            }
                          }}
                        >
                          <Checkbox
                            checked={frequencyConfig.customDays?.includes(day.value)}
                            disabled={isLoading}
                            className="pointer-events-none h-4 w-4"
                          />
                          <span className="text-xs font-medium">
                            {isRTL ? day.labelAr : day.labelEn}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Private Habit and Use Ready Habits Button in same row */}
                <div className="flex items-center gap-3">
                  <FormField
                    control={form.control}
                    name="isPrivate"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-2 space-y-0">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormLabel className={`text-sm font-medium cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`}>
                          {t('habits.privateHabit')}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  
                  <div className="flex-1"></div>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPresets(!showPresets)}
                    className="h-10 text-sm bg-[#004250] hover:bg-[#003240] text-white border-[#004250] hover:border-[#003240]"
                  >
                    {showPresets 
                      ? (isRTL ? 'إخفاء العادات الجاهزة' : 'Hide Presets')
                      : (isRTL ? 'استخدام عادات جاهزة' : 'Use Ready Habits')
                    }
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 mt-auto pt-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isLoading}
                    className="w-full sm:w-auto h-10 text-sm"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                    className="w-full sm:w-auto h-10 text-sm"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} />
                        {t('habits.creating')}
                      </>
                    ) : (
                      t('habits.create')
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </div>

          {/* Right Column: Presets - 60% width */}
          {showPresets && (
            <div className="md:w-[60%] min-w-0 flex flex-col border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-4 overflow-hidden flex-shrink-0 bg-muted/70 rounded-lg p-4 md:p-3">
              <div className="flex-1 min-h-0">
                <HabitPresetSelector onSelectPreset={handlePresetSelect} />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}